// Buffer polyfill is loaded in polyfills.js (imported in main.jsx)
// This ensures Buffer is available before Pinata SDK is imported
import pinataSDK from '@pinata/sdk';

// Initialize Pinata client
// Supports both JWT token (preferred) and API key/secret (fallback)
// Note: In Vite, env vars must have VITE_ prefix to be accessible in frontend
export const getPinataClient = () => {
  const jwtToken = import.meta.env.VITE_PINATA_JWT_TOKEN;
  const apiKey = import.meta.env.VITE_PINATA_API_KEY;
  const apiSecret = import.meta.env.VITE_PINATA_API_SECRET;

  if (jwtToken) {
    // Modern JWT-based authentication (recommended)
    return new pinataSDK({
      pinataJwt: jwtToken,
      pinataGateway: 'gateway.pinata.cloud'
    });
  } else if (apiKey && apiSecret) {
    // Legacy API key/secret authentication
    return new pinataSDK({
      pinataApiKey: apiKey,
      pinataSecretApiKey: apiSecret,
      pinataGateway: 'gateway.pinata.cloud'
    });
  } else {
    return null;
  }
};

/**
 * Publish profile data to IPFS with proper metadata tagging.
 * 
 * @param {object} profileData - The user's profile object to be published (per PRD spec)
 * @param {object} options - Additional options
 * @param {string} options.username - Profile username
 * @param {string} options.wallet - Wallet address
 * @param {string} options.env - Environment ('dev' | 'staging' | 'prod'), defaults to 'prod'
 * @returns {Promise<{cid: string|null, error: string|null}>}
 */
export const publishToIPFS = async (profileData, options = {}) => {
  const { username, wallet, env = 'prod' } = options;

  // Check if Pinata is configured
  const pinata = getPinataClient();
  if (!pinata) {
    console.warn('Pinata not configured. Using mock mode.');
    return {
      cid: 'QmMockCIDForDemoPurposesOnly123456789',
      error: null
    };
  }

  try {
    const timestamp = Date.now();
    const filename = `hubid-profile-${username || 'unknown'}-${timestamp}.json`;

    // Pin to IPFS with metadata tagging
    const result = await pinata.pinJSONToIPFS(profileData, {
      pinataMetadata: {
        name: filename,
        keyvalues: {
          app: 'hubid',
          env: env,
          type: 'profile',
          username: username || '',
          wallet: wallet?.toLowerCase() || '',
          version: '2.0'
        }
      }
    });

    console.log('✅ Published to IPFS:', result.IpfsHash);
    return {
      cid: result.IpfsHash,
      error: null
    };
  } catch (error) {
    console.error('❌ IPFS publish error:', error);
    return {
      cid: null,
      error: error.message || 'Failed to publish to IPFS'
    };
  }
};

/**
 * Fetch data from IPFS using a CID.
 * 
 * @param {string} cid - The IPFS content identifier (CID)
 * @returns {Promise<{data: object|null, error: string|null}>}
 */
export const getFromIPFS = async (cid) => {
  if (!cid || typeof cid !== 'string') {
    return {
      data: null,
      error: 'Invalid CID provided'
    };
  }

  try {
    // Try Pinata gateway first (most reliable)
    const gateways = [
      `https://gateway.pinata.cloud/ipfs/${cid}`,
      `https://ipfs.io/ipfs/${cid}`,
      `https://${cid}.ipfs.w3s.link/`
    ];

    let lastError = null;
    
    for (const gatewayUrl of gateways) {
      try {
        const response = await fetch(gatewayUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/json'
          },
          // Add timeout
          signal: AbortSignal.timeout(10000) // 10 second timeout
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        console.log('✅ Fetched from IPFS:', cid);
        return {
          data: data,
          error: null
        };
      } catch (error) {
        lastError = error;
        console.warn(`Gateway ${gatewayUrl} failed, trying next...`);
        // Continue to next gateway
      }
    }

    // All gateways failed
    throw lastError || new Error('All IPFS gateways failed');
  } catch (error) {
    console.error('❌ IPFS fetch error:', error);
    return {
      data: null,
      error: error.message || 'Failed to fetch from IPFS'
    };
  }
};